import { useEffect, useRef, useState } from 'react';
import { FileCheck, Upload, AlertCircle } from 'lucide-react';
import { Badge, Button, Card, CardBody, CardHeader, CardTitle } from '@solara/ui-shared';
import { useTranslation } from '@solara/i18n';
import { documentsApi, onboardingApi, ApiError } from '@solara/api-client';
import type { KycDocumentDto } from '@solara/api-client';

export default function KycUploadPage() {
  const { t } = useTranslation('p1');
  const [required, setRequired] = useState<string[]>([]);
  const [docs, setDocs] = useState<KycDocumentDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busyType, setBusyType] = useState<string | null>(null);
  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() => {
    void load();
  }, []);

  async function load() {
    try {
      const [req, list] = await Promise.all([onboardingApi.requiredKyc(), onboardingApi.listKyc()]);
      setRequired(req.requiredTypes);
      setDocs(list);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    }
  }

  async function handleFile(documentType: string, file: File) {
    setBusyType(documentType);
    setError(null);
    try {
      const intent = await documentsApi.uploadIntent({
        category: 'KYC',
        originalFilename: file.name,
        mimeType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
      });
      if (!intent.putUrl.startsWith('stub://')) {
        const res = await fetch(intent.putUrl, {
          method: 'PUT',
          headers: { 'Content-Type': intent.mimeType },
          body: file,
        });
        if (!res.ok) {
          throw new Error(`Upload failed (${res.status})`);
        }
      }
      await onboardingApi.recordKyc({
        documentType,
        storageKey: intent.storageKey,
        fileName: file.name,
        mimeType: intent.mimeType,
        sizeBytes: file.size,
      });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    } finally {
      setBusyType(null);
    }
  }

  const byType = new Map(docs.map((d) => [d.documentType, d]));

  return (
    <div className="space-y-6 max-w-4xl">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">{t('kyc.title')}</h1>
        <p className="text-fg-muted text-sm">{t('kyc.subtitle')}</p>
      </header>

      {error && (
        <Card className="border-danger/40 bg-danger/5 rounded-2xl">
          <CardBody className="flex items-center gap-2 text-sm text-danger">
            <AlertCircle className="h-4 w-4" />
            <span>{error}</span>
          </CardBody>
        </Card>
      )}

      <Card className="rounded-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileCheck className="h-4 w-4" />
            {t('kyc.requiredDocuments')}
          </CardTitle>
        </CardHeader>
        <CardBody>
          <ul className="divide-y divide-border">
            {required.map((type) => {
              const existing = byType.get(type);
              const disabled = busyType !== null || existing?.status === 'APPROVED';
              return (
                <li key={type} className="flex items-center justify-between py-3 text-sm">
                  <div>
                    <p className="font-medium">{type}</p>
                    <p className="text-xs text-fg-muted">
                      {t('kyc.statusLabel')}: <KycStatusBadge status={existing?.status ?? 'PENDING'} t={t} />
                      {existing?.expiryDate && (
                        <span className="ms-2">
                          {t('kyc.expires')} {new Date(existing.expiryDate).toLocaleDateString()}
                        </span>
                      )}
                    </p>
                  </div>
                  <div>
                    <input
                      type="file"
                      ref={(el) => {
                        fileInputs.current[type] = el;
                      }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) void handleFile(type, file);
                        e.target.value = '';
                      }}
                      accept="application/pdf,image/jpeg,image/png,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      className="hidden"
                    />
                    <Button
                      variant="secondary"
                      onClick={() => fileInputs.current[type]?.click()}
                      disabled={disabled}
                    >
                      <Upload className="mr-2 h-3 w-3" />
                      {existing ? t('actions.upload') + '…' : t('kyc.uploadButton')}
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </CardBody>
      </Card>
    </div>
  );
}

function KycStatusBadge({
  status,
  t,
}: {
  status: string;
  t: (k: string, vars?: Record<string, string | number>) => string;
}) {
  const tone: 'neutral' | 'success' | 'danger' | 'warning' =
    status === 'APPROVED'
      ? 'success'
      : status === 'REJECTED'
      ? 'danger'
      : status === 'EXPIRED'
      ? 'warning'
      : 'neutral';
  return (
    <Badge tone={tone} className="text-xs">
      {t(`kyc.statuses.${status}`, { defaultValue: status })}
    </Badge>
  );
}
